import { UserCircle, Wrench, LogOut, User, Settings } from "lucide-react";
import * as Sentry from "@sentry/react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ReactNode, useState, useCallback } from "react";
import { useUserProfile } from "@/contexts/ProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import { ProxyButton } from "@/components/proxy/ProxyButton";
import { ProxyExitButton } from "@/components/proxy/ProxyExitButton";
import { useProxy } from "@/contexts/ProxyContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useProfileAvatar } from "@/hooks/useProfileAvatar";
import { PlatformAdminBadge } from "./PlatformAdminBadge";
import { NotificationBadge } from "@/components/notifications/NotificationBadge";
import { useUnreadCount } from "@/hooks/useNotifications";
interface HeaderProps {
  children?: ReactNode;
}
export function Header({
  children
}: HeaderProps) {
  const { displayName, fullName, email } = useUserProfile();
  const { signOut } = useAuth();
  const { isProxyMode } = useProxy();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [isSigningOut, setIsSigningOut] = useState(false);
  
  // Fetch user's profile avatar data (image URL, initials, etc.)
  const { imageUrl, initials, fullName: avatarFullName, isLoading: avatarLoading } = useProfileAvatar();

  // Check if user can access admin features - use actual database permissions
  const canAccessAdmin = hasPermission('admin_users') || 
                      hasPermission('admin_organizations') || 
                      hasPermission('admin_audit') ||
                      hasPermission('can_manage_platform') ||
                      hasPermission('can_access_all_orgs') ||
                      hasPermission('can_view_audit_logs');
  
  // Stable click handler for avatar navigation
  const handleAvatarClick = useCallback(() => {
    navigate('/profile');
  }, [navigate]);

  // Fetch unread notification count
  const { data: unreadCount = 0 } = useUnreadCount();

  const handleNotificationsClick = useCallback(() => {
    navigate('/notifications');
  }, [navigate]);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      const { error } = await signOut();
      if (!error) {
        navigate('/login');
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'auth_error', component: 'Header', operation: 'sign_out' },
        extra: { message: 'Sign out error' }
      });
    } finally {
      setIsSigningOut(false);
    }
  };
  return <header className="h-16 border-b border-border flex items-center sticky top-0 z-20 bg-background/95 backdrop-blur-sm shadow-subtle">
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
          {children}
          <div className="font-semibold text-lg flex items-baseline text-foreground">
            <span className="text-foreground">
              mySuccession
            </span>
            <span className="text-[10px] relative -top-2 text-foreground">TM</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <PlatformAdminBadge />
          
          <NotificationBadge 
            count={unreadCount} 
            onClick={handleNotificationsClick}
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="gap-2 hover:bg-muted/80 text-foreground h-9 px-2 md:pl-3 md:pr-4 transition-all duration-150 hover:scale-[1.02]"
                aria-label={`Profile menu for ${avatarFullName}`}
              >
                <Avatar className="h-7 w-7 md:h-8 md:w-8 border border-border hover:border-primary/50 transition-colors">
                  {!avatarLoading && imageUrl && (
                    <AvatarImage 
                      src={imageUrl} 
                      alt={`Profile picture of ${avatarFullName}`}
                      className="object-cover"
                    />
                  )}
                  <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                    {avatarLoading ? '...' : initials}
                  </AvatarFallback>
                </Avatar>
                <span className="font-medium text-sm hidden md:inline-block">{fullName}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 shadow-elevated rounded-lg border p-1 bg-popover">
              <div className="px-2 py-1.5 mb-1">
                <p className="text-sm font-medium">{fullName}</p>
                <p className="text-xs text-muted-foreground">{email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="rounded-md cursor-pointer focus:bg-muted"
                onClick={handleAvatarClick}
              >
                <div className="w-full flex items-center gap-2">
                  <User className="h-4 w-4" />
                  My Profile
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="rounded-md cursor-pointer focus:bg-muted">
                <Link to="/settings" className="w-full flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {canAccessAdmin && (
                <>
                  <DropdownMenuItem className="rounded-md cursor-pointer focus:bg-muted">
                    <Link to="/admin" className="w-full flex items-center gap-2">
                      <Wrench className="h-4 w-4" />
                      Admin Center
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              
               {/* Proxy Controls */}
               {isProxyMode ? (
                 <DropdownMenuItem className="rounded-md cursor-pointer focus:bg-muted p-1">
                   <ProxyExitButton variant="ghost" size="sm" className="w-full justify-start" />
                 </DropdownMenuItem>
               ) : (
                 <DropdownMenuItem 
                   className="rounded-md cursor-pointer focus:bg-muted p-1"
                 >
                   <ProxyButton variant="header" className="w-full justify-start" />
                 </DropdownMenuItem>
               )}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="rounded-md text-muted-foreground cursor-pointer focus:bg-muted">
                <button 
                  className="w-full text-left flex items-center gap-2 disabled:opacity-50" 
                  onClick={handleSignOut}
                  disabled={isSigningOut}
                >
                  <LogOut className="h-4 w-4" />
                  {isSigningOut ? 'Signing out...' : 'Sign out'}
                </button>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>;
}
